import React from "react";
import { Lock, Mail, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const FOUNDER_EMAIL = "intel@therevenuecycleiq.com";

const PLAN_LABELS = {
  starter: "Starter",
  professional: "Professional",
  enterprise: "Enterprise",
};

/**
 * Soft-block surface shown in place of a locked feature for users on a lower plan.
 *
 * Props:
 *   - feature:        machine key (e.g. "payor_scorecards") — used in mailto subject
 *   - featureLabel:   human label (e.g. "Payor Scorecards")
 *   - description:    one-line value-prop explaining what the feature unlocks
 *   - requiredPlan:   cheapest plan that unlocks it (defaults to "professional")
 *   - currentPlan:    caller's current plan (for the "you're on X" line)
 */
export function UpgradeCard({
  feature,
  featureLabel,
  description,
  requiredPlan = "professional",
  currentPlan,
}) {
  const subject = encodeURIComponent(`Upgrade request — ${featureLabel}`);
  const body = encodeURIComponent(
    `Hi team,\n\nI'd like to upgrade to ${PLAN_LABELS[requiredPlan]} to unlock ${featureLabel}.\n\nThanks!`
  );
  const mailto = `mailto:${FOUNDER_EMAIL}?subject=${subject}&body=${body}`;

  return (
    <div
      data-testid={`upgrade-card-${feature}`}
      className="relative overflow-hidden rounded-2xl border border-teal-500/30 bg-gradient-to-br from-[#0A192F] via-[#0F2238] to-[#0A192F] p-8 sm:p-10 text-white shadow-xl"
    >
      {/* Decorative glow */}
      <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-teal-400/10 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="inline-flex items-center gap-2 rounded-full bg-teal-400/15 border border-teal-400/30 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-teal-300">
          <Lock className="h-3 w-3" />
          {PLAN_LABELS[requiredPlan]} feature
        </div>
        <h2 className="mt-5 font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">
          {featureLabel}
        </h2>
        {description ? (
          <p className="mt-3 text-sm sm:text-base text-slate-300 max-w-2xl leading-relaxed">
            {description}
          </p>
        ) : null}

        <div className="mt-6 flex items-center gap-2 text-xs text-slate-400">
          <Sparkles className="h-3.5 w-3.5 text-teal-400" />
          <span>
            You're currently on{" "}
            <span className="font-semibold text-white">
              {PLAN_LABELS[currentPlan] || "Starter"}
            </span>
            . Upgrade to{" "}
            <span className="font-semibold text-teal-300">
              {PLAN_LABELS[requiredPlan]}
            </span>{" "}
            to unlock this.
          </span>
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-3">
          <a href={mailto} data-testid={`upgrade-card-mailto-${feature}`}>
            <Button className="ai-cta rounded-lg h-11 px-6 text-sm font-semibold">
              <Mail className="h-4 w-4 mr-2" />
              Email us to upgrade
            </Button>
          </a>
          <span className="text-xs text-slate-400">{FOUNDER_EMAIL}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Detect a 403 PLAN_LOCKED response from axios and return a normalised shape
 * suitable for rendering an UpgradeCard inline. Returns null if it isn't one.
 */
export function parsePlanLockError(err) {
  // After the global axios interceptor normalises the response, the original
  // structured payload lives at `_planLock`. Fall back to `detail` for any
  // unintercepted path (e.g. SSR / unit tests).
  const data = err?.response?.data?._planLock || err?.response?.data?.detail;
  if (!data || typeof data !== "object" || data.error !== "PLAN_LOCKED") return null;
  return {
    feature: data.feature || "feature",
    featureLabel: data.feature_label || "This feature",
    requiredPlan: data.required_plan || "professional",
    currentPlan: data.current_plan || "starter",
    message: data.message || "",
  };
}
